import { useCallback, useMemo, useState } from 'react';
import { Button, Popover, Radio, Space, Typography } from 'antd';
import { PushpinOutlined, SettingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

export type ColumnPinSide = 'left' | 'right' | 'none';

export interface PinnableColumnMeta {
  key: string;
  label: string;
  /** When false, column cannot be pinned (rare). Default true. */
  pinnable?: boolean;
}

function readPins(storageKey: string, defaults: Record<string, ColumnPinSide>): Record<string, ColumnPinSide> {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return { ...defaults };
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const next = { ...defaults };
    for (const [key, value] of Object.entries(parsed)) {
      if (value === 'left' || value === 'right' || value === 'none') {
        next[key] = value;
      }
    }
    return next;
  } catch {
    return { ...defaults };
  }
}

/**
 * Persist + apply left/right column pinning for Ant Design tables.
 * Returns pin map, apply helper, and a settings popover trigger.
 */
export function useTableColumnPins(
  storageKey: string,
  columns: PinnableColumnMeta[],
  defaults: Record<string, ColumnPinSide>,
) {
  const { t } = useTranslation();
  const [pins, setPins] = useState<Record<string, ColumnPinSide>>(() =>
    readPins(storageKey, defaults),
  );

  const setPin = useCallback(
    (key: string, side: ColumnPinSide) => {
      setPins((prev) => {
        const next = { ...prev, [key]: side };
        try {
          localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          // ignore quota / private mode
        }
        return next;
      });
    },
    [storageKey],
  );

  const resetPins = useCallback(() => {
    setPins({ ...defaults });
    try {
      localStorage.setItem(storageKey, JSON.stringify(defaults));
    } catch {
      // ignore
    }
  }, [defaults, storageKey]);

  const resolveFixed = useCallback(
    (key: string): 'left' | 'right' | undefined => {
      const side = pins[key] ?? defaults[key] ?? 'none';
      if (side === 'left' || side === 'right') return side;
      return undefined;
    },
    [defaults, pins],
  );

  const settingsButton = useMemo(
    () => (
      <Popover
        trigger="click"
        placement="bottomRight"
        title={t('tableColumns.pinTitle')}
        content={
          <div style={{ width: 320, maxHeight: 420, overflowY: 'auto' }}>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 12, fontSize: 12 }}>
              {t('tableColumns.pinHint')}
            </Typography.Paragraph>
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
              {columns.map((col) => {
                if (col.pinnable === false) return null;
                const value = pins[col.key] ?? defaults[col.key] ?? 'none';
                return (
                  <div
                    key={col.key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                    }}
                  >
                    <Typography.Text ellipsis style={{ flex: 1, minWidth: 0 }}>
                      {col.label}
                    </Typography.Text>
                    <Radio.Group
                      size="small"
                      optionType="button"
                      value={value}
                      onChange={(e) => setPin(col.key, e.target.value as ColumnPinSide)}
                      options={[
                        { label: t('tableColumns.pinNone'), value: 'none' },
                        { label: t('tableColumns.pinLeft'), value: 'left' },
                        { label: t('tableColumns.pinRight'), value: 'right' },
                      ]}
                    />
                  </div>
                );
              })}
            </Space>
            <Button type="link" size="small" style={{ marginTop: 8, paddingInline: 0 }} onClick={resetPins}>
              {t('tableColumns.resetPins')}
            </Button>
          </div>
        }
      >
        <Button icon={<SettingOutlined />}>{t('tableColumns.settings')}</Button>
      </Popover>
    ),
    [columns, defaults, pins, resetPins, setPin, t],
  );

  return { pins, resolveFixed, settingsButton, setPin, resetPins };
}

/** Optional compact icon-only trigger if toolbar is crowded. */
export function TableColumnPinIcon() {
  return <PushpinOutlined />;
}
